import { useLayoutEffect, useState } from 'react';
import {
  ANCHOR_LOOKUP_INTERVAL_MS,
  visibleStepIndices,
} from '../utils/tourSteps';
import type { GuideTourStep } from './types';

function isSameList(a: readonly number[], b: readonly number[]) {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * {@link visibleStepIndices} for the running tour, checked again a few times a
 * second. Read during render it went stale: an anchor that mounts below one
 * that stays put changes nothing the provider renders from, so the counter
 * kept the old total and "Listo" could end the tour a step early.
 */
export function useVisibleSteps(
  steps: readonly GuideTourStep[] | null,
  index: number,
): number[] {
  const [visible, setVisible] = useState<number[]>([]);

  // A layout effect, so a new step never paints with the previous one's list.
  useLayoutEffect(() => {
    if (!steps) {
      setVisible([]);
      return;
    }

    const update = () => {
      const next = visibleStepIndices(steps, index);
      setVisible((prev) => (isSameList(prev, next) ? prev : next));
    };
    update();

    const timer = setInterval(update, ANCHOR_LOOKUP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [steps, index]);

  return visible;
}
